import { CreateCommandePayload } from "modules/commande/types/commande.interface";
import { FunctionComponent, useEffect, useMemo, useState } from "react";
import { Autocomplete, Option } from "components/fields/autoCompleteField";
import { useGetClients } from "modules/article/api";
import { useDebouncedSearch } from "common/hooks/src/useDebounce";

interface SelectClientProps {
  formValues: CreateCommandePayload;
  setFormValues: (values: CreateCommandePayload) => void;
}

export const SelectClient: FunctionComponent<SelectClientProps> = ({
  formValues,
  setFormValues,
}) => {
  const [searchValue, setSearchValue] = useState("");
  const debouncedSearch = useDebouncedSearch(searchValue, 300);
  const { data: clients, isLoading } = useGetClients(debouncedSearch || undefined);
  const flatClients = useMemo(() => 
    clients?.pages?.map((page) => page.data).flat() || [], 
    [clients?.pages]
  );

  const options = useMemo(() => {
    return flatClients.map((client) => ({
      id: client._id,
      name: client.name,
    }));
  }, [flatClients]);

  const handleOptionChange = (optionValue: Option) => {
    setFormValues({
      ...formValues,
      clientId: optionValue.id,
    });
  };

  useEffect(() => {
    if (searchValue === "" && formValues.clientId && options.length > 0) {
      const client = options.find(
        (option) => option.id === formValues.clientId
      );
      if (client) {
        setSearchValue(client.name);
      }
    }
  }, [formValues.clientId, options, searchValue]);

  if (options.length === 0 && !isLoading) return null;

  return (
    <Autocomplete
      options={options}
      onSelect={(option) => handleOptionChange(option)}
      value={[formValues.clientId]}
      label="Sélectionner un client"
      searchValue={searchValue}
      setSearchValue={setSearchValue}
      isLoading={isLoading}
    />
  );
};
